import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { ANALYTICS, CURSORS } from 'constants';
import utils from '@ehi/global-marketing-interface/utils';
import { dtm } from 'utils/analytics';
import { i18n } from 'utils/i18n';
import { config } from 'utils/config';
import { areDaysTimesSame, validWeekDateRange } from 'utils/date';
import PropTypes from 'prop-types';
import Button from 'components/Button';

/**
 * BranchHours - used on the store finder and used on the branch pages that are generated in HTL for SEO.  This displays
 *               the hours of a location
 * @param {object} props           - React Props
 * @param {string} props.hideTodaysTimes - if we should hide todays times
 * @param {string} props.getBranchHours  - function to get branch hours
 * @param {string} props.reservationFlow - if we are in reservation flow
 * @param {string} props.showReturnLocation if we should show the return location
 * @param {string} props.pickUpTime       - the pick up time
 * @param {string} props.dropOffTime      - the dropoff time
 * @return {JSX} Branch Hours Component
 */

// Getting the start of the week according GMA
const startOfWeek = config.getWeek('dow');

const BranchHours = ({
  hideTodaysTimes,
  getBranchHours,
  reservationFlow,
  showReturnLocation,
  pickUpTime,
  dropOffTime,
  hoursForStore,
  id,
}) => {
  const getLeadDay = () =>
    reservationFlow
      ? utils.getDateTimeObjFromTs((showReturnLocation && dropOffTime.split('T')[0]) || pickUpTime.split('T')[0])
      : utils.getCurrentDateTimeObj();

  const initialLeadDay = getLeadDay();

  const [isLocationDataLoaded, setIsLocationDataLoaded] = useState(false);

  //  If this is in StoreFinder use current day - otherwise cal it off of what date is coming down from ResFlow
  //  The leadDay pattern is the driver for this whole component.  When we have the lead day we use the var `arrayToPrint`
  //  go from the leadDay till the day we 'want'.  We store the results in `arrayToPrint`
  const [leadDay, setLeadDay] = useState(initialLeadDay);

  //  The hours come down as an object with the key as the day, we use this slice of state
  //  to quickly get the hours for that day
  const currentDayHash = initialLeadDay.format('YYYY-MM-DD');

  //  This is the same day as the hash, just in a readable format
  const currentDayReadable = initialLeadDay.format('LL');
  const [currentHours, setCurrentHours] = useState([]);
  const [readableDateRange, setReadableDateRange] = useState('');
  const [canGoForward, setCanGoForward] = useState(true);
  const [canGoBackwards, setCanGoBackwards] = useState(true);

  // This takes the lead day and builds the days we should print out
  const setUpTimeTable = (leadDayParam) => {
    const arrayToPrint = [];

    for (let i = 0; i < 7; i += 1) {
      const dayInUseableFormat = utils.getDateTimeObjFromTs(leadDayParam).add(i, 'days');
      const readableDate = dayInUseableFormat.format('dddd');
      const hashForDate = dayInUseableFormat.format('YYYY-MM-DD');
      const latestIndex = arrayToPrint[arrayToPrint.length - 1];

      //  The logic in these if statements covers the the fact if two days have the same hours and they are next to each other
      //  we show them like `DAY - DAY: <SAME TIMES>`
      if (arrayToPrint.length === 0 || !areDaysTimesSame(hashForDate, latestIndex.hashForDate, hoursForStore)) {
        arrayToPrint.push({ hashForDate, readableDate, dayInUseableFormat, hasRepeatTimesOnDay: false });
      } else {
        //  Times are the same we should print them like DAY - DAY:
        latestIndex.readableDate = `${latestIndex.dayInUseableFormat.format('dddd')} - ${readableDate}`;
        latestIndex.lastMomentObjectInSameDateRange = dayInUseableFormat;
        latestIndex.hasRepeatTimesOnDay = true;
      }
      //  If the day we are looking at is before the present day we disregard it
      if (utils.getDateTimeObjFromTs(hashForDate).isBefore(utils.getCurrentDateTimeObj().format('YYYY-MM-DD'))) {
        arrayToPrint.pop();
      }

      //  We stop one day before the start of the week (defined by ehi object)
      //  This way the arrayToPrint, which holds the times to print out stops right before
      //  the start of the week.  That way every time we go forward or backward we always start with the
      //  start of the week AND don't have two 'starts of the week' in one time table.
      //  EX: Mon,Tues,Wed, Thur, Fri, Sat,Sun, Mon <= Mon being the start of the week
      if (dayInUseableFormat.day() === startOfWeek - 1) {
        //  We have hit the start of the week
        break;
      }
    }

    //  Can the user go forward or backwards
    const canGoNext = validWeekDateRange(
      utils.getDateTimeObjFromTs(arrayToPrint[0]?.dayInUseableFormat).add(1, 'week'),
      hoursForStore
    );
    const canGoPrevious = validWeekDateRange(
      utils.getDateTimeObjFromTs(arrayToPrint[0]?.dayInUseableFormat).subtract(1, 'week'),
      hoursForStore
    );

    const head = utils.getDateTimeObjFromTs(arrayToPrint[0]?.dayInUseableFormat).format('LL');
    const latest = arrayToPrint[arrayToPrint.length - 1];
    const tail = latest?.hasRepeatTimesOnDay
      ? utils.getDateTimeObjFromTs(latest.lastMomentObjectInSameDateRange).format('LL')
      : latest?.dayInUseableFormat.format('LL');

    setCurrentHours(arrayToPrint);
    setReadableDateRange(`${head} - ${tail}`);
    setCanGoForward(canGoNext);
    setCanGoBackwards(canGoPrevious);
  };

  useEffect(() => {
    if (hoursForStore === null) {
      getBranchHours(id);
    } else {
      setIsLocationDataLoaded(true);
      setUpTimeTable(leadDay);
    }
  }, []);

  useEffect(() => {
    if (hoursForStore !== null && !isLocationDataLoaded) {
      setIsLocationDataLoaded(true);
      setUpTimeTable(leadDay);
    }
  }, [hoursForStore]);

  const correctLeadDay = (oldLeadDay) => {
    let lead = oldLeadDay;
    while (
      utils.getDateTimeObjFromTs(lead).day().toString() !== startOfWeek ||
      utils.getDateTimeObjFromTs(lead).isBefore(utils.getCurrentDateTimeObj())
    ) {
      lead = utils.getDateTimeObjFromTs(lead).add(1, 'days');
    }
    return utils.getDateTimeObjFromTs(lead);
  };

  const handleForwardMonth = () => {
    const leadDayDayOfWeek = utils.getDateTimeObjFromTs(leadDay).day();
    // If we are already at the correct start of the week just add seven days
    // other wise keep adding days until we get to the start day
    const dayInUseableFormat =
      leadDayDayOfWeek.toString() === startOfWeek
        ? utils.getDateTimeObjFromTs(leadDay).add(1, 'week')
        : correctLeadDay(leadDay);

    const newLeadDay = utils.getDateTimeObjFromTs(dayInUseableFormat);
    setLeadDay(newLeadDay);
    setUpTimeTable(newLeadDay);
  };

  const handleBackMonth = () => {
    const dayInUseableFormat = utils.getDateTimeObjFromTs(leadDay).subtract(1, 'week');
    const newLeadDay = utils.getDateTimeObjFromTs(dayInUseableFormat);
    setLeadDay(newLeadDay);
    setUpTimeTable(newLeadDay);
  };

  const formatToReadableTime = (time) => utils.getDateTimeObjFromTs(time, 'HH:mm').format('LT');

  const formatHours = (hoursObject, className = '') => {
    if (!hoursObject) return null;
    if (hoursObject.hours.length === 0) {
      if (hoursObject.closed) {
        return <p className={className}>{i18n('location_finder_timetable_closed')}</p>;
      }
      if (hoursObject.open24Hours) {
        return <p className={className}>{i18n('location_finder_timetable_open_24_hours')}</p>;
      }
      return null;
    }
    return hoursObject.hours.map(({ open, close }, index) => (
      <p key={index} className={className}>
        {`${formatToReadableTime(open)} ${i18n('map_locations_between_time')} `}
        <span className='map-location-detail-tile-expanded-state__time-close'>{formatToReadableTime(close)}</span>
      </p>
    ));
  };

  //  function for printing the hours out
  const printHours = () =>
    currentHours &&
    currentHours.map(
      ({ hashForDate, readableDate }, idx) =>
        hoursForStore &&
        hoursForStore[hashForDate] && (
          <div key={`${idx}--${hashForDate}`} className='map-location-detail-tile-expanded-state__time-container'>
            <p className='map-location-detail-tile-expanded-state__date'>{readableDate}</p>
            <div className='map-location-detail-tile-expanded-state__time-hours'>
              {formatHours(hoursForStore[hashForDate][CURSORS.STANDARD])}
            </div>
          </div>
        )
    );

  // Calender UI
  const renderBranchHoursUI = () => (
    <>
      <div
        className={cn('map-location-detail-tile-expanded-state__time-header', {
          stack:
            hoursForStore &&
            !hideTodaysTimes &&
            hoursForStore[currentDayHash] &&
            hoursForStore[currentDayHash][CURSORS.STANDARD]?.hours?.length > 1,
        })}
      >
        {!hideTodaysTimes && hoursForStore && hoursForStore[currentDayHash] && (
          <>
            <p className='map-location-detail-tile-expanded-state__current-date'>{currentDayReadable}</p>
            <div>
              {formatHours(
                hoursForStore[currentDayHash][CURSORS.STANDARD],
                'map-location-detail-tile-expanded-state__current-time'
              )}
            </div>
          </>
        )}
      </div>

      <div
        className={cn('map-location-detail-tile-expanded-state__location-hours-container', {
          'map-location-detail-tile-expanded-state__location-hours-container--on-branch-page': hideTodaysTimes,
        })}
      >
        <p
          className={cn('map-location-detail-tile-expanded-state__current-week-hours', {
            'map-location-detail-tile-expanded-state__hours-header-text-branch-page': hideTodaysTimes,
          })}
        >
          {`${i18n('map_locations_tiles_expanded_state_prefix_current_week')} ${readableDateRange}`}
        </p>
        {printHours()}
        <div
          className={cn({
            'map-location-detail-tile-expanded-state__time-table-time-group': canGoBackwards && canGoForward,
            'map-location-detail-tile-expanded-state__time-table-time-group--forward': !canGoBackwards && canGoForward,
            'map-location-detail-tile-expanded-state__time-table-time-group--backward': canGoBackwards && !canGoForward,
          })}
        >
          {canGoBackwards && (
            <Button
              plain
              data-dtm-track={dtm(ANALYTICS.UI_BUTTON, ANALYTICS.HOURS_OF_OPERATION, ANALYTICS.PREVIOUS)}
              onClick={handleBackMonth}
              aria-label={i18n('map_locations_tiles_expanded_state_previous_week_hours')}
              className={cn('link map-location-detail-tile-expanded-state__cycle-week-forward', {
                'map-location-detail-tile-expanded-state__location-details': hideTodaysTimes,
              })}
            >
              {i18n('map_locations_tiles_expanded_state_previous_week_hours')}
            </Button>
          )}
          {canGoForward && (
            <Button
              plain
              data-dtm-track={dtm(ANALYTICS.UI_BUTTON, ANALYTICS.HOURS_OF_OPERATION, ANALYTICS.NEXT)}
              onClick={handleForwardMonth}
              aria-label={i18n('map_locations_tiles_expanded_state_future_week_hours')}
              className={cn('link map-location-detail-tile-expanded-state__cycle-week-backwards', {
                'map-location-detail-tile-expanded-state__location-details': hideTodaysTimes,
              })}
            >
              {i18n('map_locations_tiles_expanded_state_future_week_hours')}
            </Button>
          )}
        </div>
      </div>
    </>
  );

  return isLocationDataLoaded ? renderBranchHoursUI() : null;
};

BranchHours.propTypes = {
  hideTodaysTimes: PropTypes.bool,
  getBranchHours: PropTypes.func,
  reservationFlow: PropTypes.bool,
  showReturnLocation: PropTypes.bool,
  pickUpTime: PropTypes.string,
  dropOffTime: PropTypes.string,
  hoursForStore: PropTypes.object,
  id: PropTypes.string,
};

export default BranchHours;
